import type { FeedbackAttachmentKind, FeedbackContext } from '@erp/rpc';
import { fileMatchesAccept } from '@/lib/file-accept';

/**
 * Pure rules for what a feedback may carry. No React, no DOM access beyond
 * the `File`/`Blob` types, so every branch is unit-testable.
 */

/** Mirrors the server's FEEDBACK_IMAGE_CONTENT_TYPE_RE. */
export const FEEDBACK_IMAGE_ACCEPT =
  'image/png,image/jpeg,image/gif,image/webp';
export const MAX_FEEDBACK_IMAGES = 20;
export const MAX_FEEDBACK_IMAGE_BYTES = 10 * 1024 * 1024;

export type ImageRejectionReason = 'not_image' | 'too_large' | 'too_many';

export interface ImageRejection {
  file: File;
  reason: ImageRejectionReason;
}

/**
 * Which of the picked files may be attached, given how many are already
 * queued. Order is preserved; the first files fill the remaining slots.
 */
export function validateFeedbackImages(
  files: readonly File[],
  existingCount: number,
): { accepted: File[]; rejected: ImageRejection[] } {
  const accepted: File[] = [];
  const rejected: ImageRejection[] = [];
  let slots = Math.max(0, MAX_FEEDBACK_IMAGES - existingCount);
  for (const file of files) {
    if (!fileMatchesAccept(file, FEEDBACK_IMAGE_ACCEPT)) {
      rejected.push({ file, reason: 'not_image' });
    } else if (file.size > MAX_FEEDBACK_IMAGE_BYTES) {
      rejected.push({ file, reason: 'too_large' });
    } else if (slots === 0) {
      rejected.push({ file, reason: 'too_many' });
    } else {
      accepted.push(file);
      slots -= 1;
    }
  }
  return { accepted, rejected };
}

export interface BrowserEnvironment {
  userAgent: string;
  screenWidth: number;
  screenHeight: number;
  viewportWidth: number;
  viewportHeight: number;
  locale?: string;
  now: Date;
}

export function buildFeedbackContext(env: BrowserEnvironment): FeedbackContext {
  return {
    userAgent: env.userAgent.slice(0, 1024),
    screenWidth: Math.round(env.screenWidth),
    screenHeight: Math.round(env.screenHeight),
    viewportWidth: Math.round(env.viewportWidth),
    viewportHeight: Math.round(env.viewportHeight),
    timestamp: env.now.toISOString(),
    ...(env.locale ? { locale: env.locale.slice(0, 32) } : {}),
  };
}

export function readBrowserEnvironment(): BrowserEnvironment {
  return {
    userAgent: navigator.userAgent,
    screenWidth: window.screen.width,
    screenHeight: window.screen.height,
    viewportWidth: window.innerWidth,
    viewportHeight: window.innerHeight,
    locale: navigator.language,
    now: new Date(),
  };
}

/** Page fields the dialog sends; trimmed and bounded like the contract. */
export function buildPageInfo(
  url: string | null | undefined,
  title: string | null | undefined,
): { pageUrl?: string; pageTitle?: string } {
  const pageUrl = url?.trim().slice(0, 2048);
  const pageTitle = title?.trim().slice(0, 512);
  return {
    ...(pageUrl ? { pageUrl } : {}),
    ...(pageTitle ? { pageTitle } : {}),
  };
}

export function screenshotFileName(now: Date, index: number): string {
  const stamp = now.toISOString().replace(/[:.]/g, '-');
  return `screenshot-${index + 1}-${stamp}.jpg`;
}

export interface CapturedScreenshot {
  id: string;
  blob: Blob;
  previewUrl: string;
  /** Where the user was when this shot was taken. */
  pageUrl: string;
  pageTitle: string;
}

export interface PendingAttachment {
  id: string;
  file: File;
  previewUrl: string;
}

export interface SubmitAttachment {
  blob: Blob;
  fileName: string;
  contentType: string;
  kind: FeedbackAttachmentKind;
}

/**
 * The upload list in send order: captured page screenshots first, in the
 * order they were taken, then the user's own files.
 */
export function collectSubmitAttachments(
  screenshots: readonly Pick<CapturedScreenshot, 'blob'>[],
  files: readonly PendingAttachment[],
  now: Date,
): SubmitAttachment[] {
  const list: SubmitAttachment[] = screenshots.map(({ blob }, index) => ({
    blob,
    fileName: screenshotFileName(now, index),
    contentType: blob.type || 'image/jpeg',
    kind: 'auto_screenshot',
  }));
  for (const { file } of files) {
    list.push({
      blob: file,
      fileName: file.name,
      contentType: file.type || 'image/png',
      kind: 'upload',
    });
  }
  return list;
}
